import { t } from "i18next";

import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";

interface ProfileLoaderProps {
  className?: string;
}

export default function ProfileLoader({ className }: ProfileLoaderProps) {
  return (
    <ScrollArea className="h-[calc(100dvh_-_4rem)]">
      <title>{`${t("profile:profilePage")} - Ximly`}</title>
      <div
        className={cn(
          "bg-background flex min-h-dvh flex-col items-center justify-center gap-4 space-y-6 px-1 sm:px-8 xl:flex-row xl:items-start",
          className,
        )}
      >
        <div className="w-full space-y-6 lg:w-[50rem]">
          {/* Header Section Skeleton */}
          <div className="relative h-[360px] w-full rounded-lg border">
            {/* Banner */}
            <Skeleton className="h-[200px] w-full" />

            {/* Avatar */}
            <div className="absolute top-32 left-6">
              <Skeleton className="h-32 w-32 rounded-lg" />
            </div>

            {/* Name and Location */}
            <div className="mt-16 space-y-2 px-8">
              <Skeleton className="h-12 w-48" />
              <Skeleton className="h-8 w-64" />
            </div>
          </div>

          <div className="container mx-auto max-w-3xl">
            <div className="w-full space-y-6">
              {/* About Section Skeleton */}
              <div className="flex h-[292px] flex-col rounded-lg border p-6 shadow-xs">
                <div className="mb-4 flex items-center gap-2">
                  <Skeleton className="h-6 w-6 rounded-full" />
                  <Skeleton className="h-6 w-32" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
                <div className="mt-auto mb-4 space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </div>

              {/* Education Section Skeleton */}
              <div className="h-[212px] rounded-lg border p-6 shadow-xs">
                <div className="mb-4 flex items-center gap-2">
                  <Skeleton className="h-6 w-6 rounded-full" />
                  <Skeleton className="h-6 w-40" />
                </div>
                <div className="flex items-center justify-center py-8">
                  <Skeleton className="h-6 w-32" />
                </div>
              </div>

              {/* Certificates Section Skeleton */}
              <div className="h-[212px] rounded-lg border p-6 shadow-xs">
                <div className="mb-4 flex items-center gap-2">
                  <Skeleton className="h-6 w-6 rounded-full" />
                  <Skeleton className="h-6 w-36" />
                </div>
                <div className="space-y-4">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              </div>

              {/* Subjects Section Skeleton */}
              <div className="h-[300px] rounded-lg border p-6 shadow-xs">
                <div className="mb-4 flex items-center gap-2">
                  <Skeleton className="h-6 w-6 rounded-full" />
                  <Skeleton className="h-6 w-36" />
                </div>
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-8 w-32" />
                  ))}
                </div>
              </div>

              {/* Languages Section Skeleton */}
              <div className="rounded-lg border p-6 shadow-xs">
                <div className="mb-4 flex items-center gap-2">
                  <Skeleton className="h-6 w-6 rounded-full" />
                  <Skeleton className="h-6 w-36" />
                </div>
                <div className="space-y-4">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="flex w-full max-w-4xl flex-col gap-4 sm:min-w-[23rem] lg:w-[50rem] xl:w-fit">
          {/* Settings Card Skeleton */}
          <div className="rounded-lg border p-6 shadow-xs xl:w-[23rem]">
            <div className="mb-4 flex items-center gap-2">
              <Skeleton className="h-6 w-6 rounded-full" />
              <Skeleton className="h-6 w-36" />
            </div>

            {/* Language Selection */}
            <div className="mb-6 space-y-2">
              <Skeleton className="h-5 w-40" />
              <div className="flex gap-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-10 w-16" />
                ))}
              </div>
            </div>

            {/* Public Profile URL */}
            <div className="space-y-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-6 w-full" />
            </div>
          </div>

          {/* YouTube Preview Skeleton */}
          <div className="rounded-lg border p-2 shadow-xs">
            <Skeleton className="h-48 w-full" />
          </div>
          <div className="flex flex-col gap-2 rounded-lg border p-2 shadow-xs">
            <Skeleton className="h-6 w-1/2" />
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}
